'use client';

import { AlertTriangle, ArrowRightCircle, CheckCircle2, FileText, Info, Loader2, Save, X } from 'lucide-react';
export const EMPTY_COMMON_NOTICE_DIALOG = {
  open: false,
  title: '알림',
  subtitle: '부동산-AUTO 알림',
  message: '',
  detail: '',
  tone: 'info',
  confirmLabel: '확인',
  cancelLabel: '',
  secondaryLabel: ''
};
export const EMPTY_AUTOMATION_COMPLETION_NOTICE = {
  open: false,
  variant: 'info',
  title: '',
  message: '',
  detail: '',
  contractId: ''
};
export const EMPTY_REGISTRY_ISSUE_PAYMENT_CONFIRM = {
  open: false,
  paymentInput: null,
  address: '',
  amount: 0
};
export const EMPTY_REGISTRY_ISSUE_PAYMENT_NOTICE = {
  open: false,
  contractId: '',
  address: '',
  paymentId: '',
  orderId: '',
  amount: 0,
  paidAt: '',
  paidAtKst: '',
  receiptUrl: '',
  message: ''
};
const toneConfig = {
  info: {
    icon: Info,
    iconClass: 'bg-blue-50 text-blue-700',
    confirmClass: 'bg-blue-700 text-white hover:bg-blue-800'
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'bg-amber-50 text-amber-700',
    confirmClass: 'bg-amber-600 text-white hover:bg-amber-700'
  },
  danger: {
    icon: AlertTriangle,
    iconClass: 'bg-rose-50 text-rose-700',
    confirmClass: 'bg-rose-700 text-white hover:bg-rose-800'
  },
  success: {
    icon: CheckCircle2,
    iconClass: 'bg-emerald-50 text-emerald-700',
    confirmClass: 'bg-emerald-700 text-white hover:bg-emerald-800'
  }
};
function NoticeHeader({
  logoSrc,
  subtitle,
  onClose
}) {
  return <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <img src={logoSrc} alt="이음" className="h-7 w-auto shrink-0 object-contain" />
        <div className="min-w-0">
          <div className="truncate text-sm font-black text-slate-950">부동산-AUTO 알림</div>
          <div className="truncate text-xs font-semibold text-slate-500">{subtitle}</div>
        </div>
      </div>
      <button type="button" onClick={onClose} className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700" title="닫기">
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>;
}
export function RealtorAutoCommonNoticeDialog({
  open,
  logoSrc,
  title = '알림',
  subtitle = '부동산-AUTO 알림',
  message,
  detail,
  tone = 'info',
  confirmLabel = '확인',
  cancelLabel,
  secondaryLabel,
  onConfirm,
  onCancel,
  onSecondary
}) {
  if (!open) return null;
  const toneStyle = toneConfig[tone] ?? toneConfig.info;
  const Icon = toneStyle.icon;
  const hasCancel = Boolean(onCancel && cancelLabel);
  const hasSecondary = Boolean(onSecondary && secondaryLabel);
  const handleDismiss = onCancel || onConfirm;
  return <div className="fixed inset-0 z-[220] flex items-center justify-center bg-slate-950/35 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="realtorauto-common-notice-title" data-realtorauto-common-notice-dialog>
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
        <NoticeHeader logoSrc={logoSrc} subtitle={subtitle} onClose={handleDismiss} />
        <div className="space-y-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${toneStyle.iconClass}`}>
              <Icon className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <div id="realtorauto-common-notice-title" className="text-base font-black text-slate-950">
                {title}
              </div>
              <div className="mt-1 whitespace-pre-line text-sm font-semibold leading-6 text-slate-600">{message}</div>
            </div>
          </div>
          {detail ? <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm font-bold leading-6 text-blue-900">
              {detail}
            </div> : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-white px-4 py-3">
          {hasCancel ? <button type="button" onClick={onCancel} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 hover:bg-slate-50">
              <X className="h-4 w-4" aria-hidden />
              {cancelLabel}
            </button> : null}
          {hasSecondary ? <button type="button" onClick={onSecondary} className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-blue-200 bg-white px-4 text-sm font-bold text-blue-700 hover:bg-blue-50">
              <ArrowRightCircle className="h-4 w-4" aria-hidden />
              {secondaryLabel}
            </button> : null}
          <button type="button" onClick={onConfirm} className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-bold ${toneStyle.confirmClass}`}>
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>;
}
export function RealtorAutoAutomationCompletionNoticeDialog({
  notice,
  logoSrc,
  onClose,
  onConfirm
}) {
  if (!notice.open) return null;
  return <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/35 px-4 py-6" data-automation-completion-notice role="dialog" aria-modal="true" aria-label="계약방 작업 완료 알림">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
        <NoticeHeader logoSrc={logoSrc} subtitle="계약방 작업 상태" onClose={onClose} />
        <div className="space-y-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <div className="text-base font-black text-slate-950">{notice.title}</div>
              <div className="mt-1 text-sm font-semibold leading-6 text-slate-600">{notice.message}</div>
            </div>
          </div>
          {notice.detail ? <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm font-bold leading-6 text-blue-900">
              {notice.detail}
            </div> : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-4 py-3">
          <button type="button" onClick={onClose} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 hover:bg-slate-50">
            <X className="h-4 w-4" aria-hidden />
            닫기
          </button>
          <button type="button" onClick={onConfirm} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-bold text-white hover:bg-blue-800">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            확인
          </button>
        </div>
      </div>
    </div>;
}
export function RealtorAutoRegistryIssuePaymentConfirmDialog({
  state,
  logoSrc,
  onClose,
  onConfirm
}) {
  if (!state.open) return null;
  return <div className="fixed inset-0 z-[126] flex items-center justify-center bg-slate-950/35 px-4 py-6" data-registry-issue-payment-confirm role="dialog" aria-modal="true" aria-label="등기부등본 발급 결제 안내">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
        <NoticeHeader logoSrc={logoSrc} subtitle="등기부등본 발급 결제 안내" onClose={onClose} />
        <div className="space-y-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
              <FileText className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <div className="text-base font-black text-slate-950">등기부등본 발급 결제</div>
              <p className="mt-1 text-sm font-semibold leading-6 text-slate-700">
                {state.amount.toLocaleString('ko-KR')}원 결제 후 현 매물의 등기부등본 발급 및 자동화 분석이 가능합니다.
              </p>
            </div>
          </div>
          {state.address ? <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm font-bold leading-6 text-blue-900">
              {state.address}
            </div> : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-4 py-3">
          <button type="button" onClick={onClose} className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 hover:bg-slate-50">
            취소
          </button>
          <button type="button" onClick={onConfirm} className="inline-flex h-10 items-center justify-center rounded-lg bg-blue-700 px-4 text-sm font-bold text-white hover:bg-blue-800">
            결제
          </button>
        </div>
      </div>
    </div>;
}
export function RealtorAutoRegistryIssuePaymentNoticeDialog({
  notice,
  logoSrc,
  paidAtLabel,
  onClose
}) {
  if (!notice.open) return null;
  return <div className="fixed inset-0 z-[125] flex items-center justify-center bg-slate-950/35 px-4 py-6" data-registry-issue-payment-notice role="dialog" aria-modal="true" aria-label="등기부등본 결제 완료 알림">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
        <NoticeHeader logoSrc={logoSrc} subtitle="등기부등본 결제 상태" onClose={onClose} />
        <div className="space-y-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <div className="text-base font-black text-slate-950">등기부등본 결제 완료</div>
              <div className="mt-1 whitespace-pre-line text-sm font-semibold leading-6 text-slate-600">
                {notice.message}
              </div>
            </div>
          </div>
          {notice.address ? <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm font-bold leading-6 text-blue-900">
              {notice.address}
            </div> : null}
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate-500">
            <span>결제시각 {paidAtLabel}</span>
            {notice.receiptUrl ? <a href={notice.receiptUrl} target="_blank" rel="noreferrer" className="text-blue-700 underline underline-offset-2">
                영수증 열기
              </a> : null}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-4 py-3">
          <button type="button" onClick={onClose} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-bold text-white hover:bg-blue-800">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            확인
          </button>
        </div>
      </div>
    </div>;
}
export function RealtorAutoRegistryCacheFreeViewConfirmDialog({
  open,
  onResolve
}) {
  if (!open) return null;
  return <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/45 px-4 py-6" data-registry-cache-free-view-confirm role="dialog" aria-modal="true" aria-label="물건지 업데이트 확인">
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-5 shadow-xl">
        <h2 className="text-base font-semibold text-slate-950">물건지 업데이트</h2>
        <p className="mt-3 text-sm font-medium leading-6 text-slate-700">
          해당 등기부로 물건지 업데이트를 진행하시겠습니까?
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => onResolve(false)} className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            아니오
          </button>
          <button type="button" onClick={() => onResolve(true)} className="inline-flex h-9 items-center justify-center rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white hover:bg-blue-800">
            예
          </button>
        </div>
      </div>
    </div>;
}
export function RealtorAutoAddressReselectConfirmDialog({
  open,
  logoSrc,
  selectedDisplayAddress,
  selectedJibunLine,
  onCancel,
  onConfirm
}) {
  if (!open) return null;
  return <div className="fixed inset-0 z-[200] flex items-end justify-center bg-slate-950/45 px-3 py-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="address-reselect-confirm-title" data-address-reselect-confirm-modal>
      <section className="w-full max-w-sm overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white">
              <img src={logoSrc} alt="이음" className="h-8 w-auto object-contain" />
            </div>
            <div className="min-w-0">
              <h2 id="address-reselect-confirm-title" className="text-base font-black text-slate-950">
                주소 재검색
              </h2>
              <p className="mt-1 text-xs font-bold text-slate-500">이음-부동산 AUTO</p>
            </div>
          </div>
        </div>
        <div className="px-5 py-5">
          <p className="text-sm font-bold leading-6 text-slate-800">다른 주소를 검색하시겠습니까?</p>
          {selectedDisplayAddress ? <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-black text-slate-500">현재 선택 주소</div>
              <div className="mt-1 text-xs font-bold leading-5 text-slate-800">{selectedDisplayAddress}</div>
              {selectedJibunLine ? <div className="mt-1 text-[11px] font-bold leading-4 text-slate-500">{selectedJibunLine}</div> : null}
            </div> : null}
        </div>
        <div className="grid grid-cols-2 gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4">
          <button type="button" onClick={onCancel} className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-black text-slate-700 hover:bg-slate-100">
            아니오
          </button>
          <button type="button" onClick={onConfirm} className="inline-flex h-10 items-center justify-center rounded-lg border border-blue-700 bg-blue-700 px-3 text-sm font-black text-white hover:bg-blue-800">
            예
          </button>
        </div>
      </section>
    </div>;
}
export function RealtorAutoTemplateUnsavedNavigationDialog({
  open,
  versionName,
  saving,
  canSave,
  onVersionNameChange,
  onCancel,
  onDiscard,
  onSave
}) {
  if (!open) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6" data-template-unsaved-modal>
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-xl">
        <div className="text-base font-black text-slate-950">저장하시겠습니까?</div>
        <p className="mt-2 text-sm leading-6 text-slate-500">
          현재 템플릿에 저장하지 않은 변경사항이 있습니다. 저장하려면 버전 이름을 입력하고 저장하세요.
        </p>
        <label className="mt-4 grid gap-1 text-xs font-bold text-slate-600">
          버전 이름
          <input value={versionName} onChange={event => onVersionNameChange(event.target.value)} className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-900 outline-none focus:border-blue-400" placeholder="예: 계약 문구 수정" autoFocus data-template-unsaved-version-name />
        </label>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onCancel} className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            계속 편집
          </button>
          <button type="button" onClick={onDiscard} className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            아니오
          </button>
          <button type="button" onClick={() => void onSave()} disabled={!canSave || saving} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
            저장하기
          </button>
        </div>
      </div>
    </div>;
}
export function RealtorAutoRegistryReviewTaskDetailDialog({
  open,
  label,
  value,
  isOk,
  severityLabel,
  matchLabel,
  badgeClassName,
  cardClassName,
  normalizedRightDetail,
  detailRows,
  onClose
}) {
  if (!open) return null;
  return <div className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-950/45 px-4 py-6" data-registry-review-task-detail-modal>
      <div className="flex max-h-[86vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${isOk ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              {isOk ? <CheckCircle2 className="h-5 w-5" aria-hidden /> : <AlertTriangle className="h-5 w-5" aria-hidden />}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-black text-slate-950">{label}</h2>
                {severityLabel ? <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-black ${badgeClassName}`}>
                    {severityLabel}
                  </span> : null}
                {matchLabel ? <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-black ${badgeClassName}`}>
                    {matchLabel}
                  </span> : null}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" title="닫기">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-3 text-sm font-semibold leading-6 ${cardClassName}`}>
            <span className="min-w-0 whitespace-pre-wrap break-words">{value}</span>
            {matchLabel ? <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-black ${badgeClassName}`}>
                {matchLabel}
              </span> : null}
          </div>
          {normalizedRightDetail}
          {detailRows}
        </div>
        <div className="flex justify-end border-t border-slate-100 px-5 py-3">
          <button type="button" onClick={onClose} className="inline-flex h-9 items-center justify-center rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white hover:bg-blue-800">
            확인
          </button>
        </div>
      </div>
    </div>;
}
export function RealtorAutoPartyCautionDetailDialog({
  open,
  label,
  value,
  ok,
  statusLabel,
  badgeClassName,
  cardClassName,
  detailRows,
  onClose
}) {
  if (!open) return null;
  return <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/45 px-4 py-6" data-party-caution-detail-modal role="dialog" aria-modal="true" aria-label="권리 주의사항 상세">
      <div className="flex max-h-[86vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              {ok ? <CheckCircle2 className="h-5 w-5" aria-hidden /> : <AlertTriangle className="h-5 w-5" aria-hidden />}
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-black text-slate-950">{label}</h2>
              <span className={`mt-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-black ${badgeClassName}`}>
                {statusLabel}
              </span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" title="닫기">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className={`rounded-lg border px-3 py-3 text-sm font-semibold leading-6 ${cardClassName}`}>
            {value}
          </div>
          {detailRows}
        </div>
        <div className="flex justify-end border-t border-slate-100 px-5 py-3">
          <button type="button" onClick={onClose} className="inline-flex h-9 items-center justify-center rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white hover:bg-blue-800">
            확인
          </button>
        </div>
      </div>
    </div>;
}